import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import FormContainer from "src/components/Displays/FormContainer";
import FormSubmitContainer from "src/components/Shared/FormSubmitContainer";
import { ROUTES } from "src/consts/routes";
import { BONUS, SESSION_KEYS, SITE_TECHNICAL_DATA } from "src/consts/session";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { GlobalContextProps } from "src/components/GlobalContext/GlobalContext";
import { smartMeterFormSchema } from "src/schema/smartMeterFormSchema";
import SmartMeterForm from "src/components/Forms/SmartMeterForm/SmartMeterForm";
import { tariffSavings, TariffSavingsPayload } from "src/api/apiClients";
import { Tariff } from "src/models/clientsModel";
import { tariffSavingsMapped } from "src/dto/clientsModelMapped";
import { useState } from "react";
import usePayloadMapped from "src/dto/usePayloadMapped";
import { MSG_IDS } from "src/consts/msgIds";
import { useContext } from "react";
import { ThemeContext } from "..//components/ThemeUsage/ThemeUsage";

export enum SmartMeterTypes {
  JA = "true",
  NEIN = "false",
  BLANK = "",
}

export interface SmartMeterFormValues {
  smartMeterInstalled: SmartMeterTypes;
}

const SmartMeter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { siteTechnicalData: siteTechnicalDataFromStorage } =
    usePayloadMapped();

  const { setAnimations } = useOutletContext<GlobalContextProps>();

  const [getSiteTechnicalData, setSiteTechnicalData] =
    useSessionStorage<SITE_TECHNICAL_DATA>(SESSION_KEYS.SITE_TECHNICAL_DATA);

  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  const [getTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);
  const [getSalesPartnerId] = useSessionStorage<string>(
    SESSION_KEYS.SALES_PARTNER_ID,
  );

  const transferId = getTransferId();
  const salesPartnerId = getSalesPartnerId();
  const themeName = useContext(ThemeContext);

  const form = useForm<SmartMeterFormValues>({
    defaultValues: {
      smartMeterInstalled:
        (getSiteTechnicalData()?.smartMeterInstalled as SmartMeterTypes) ||
        SmartMeterTypes.BLANK,
    },
    mode: "onSubmit",
    resolver: yupResolver(smartMeterFormSchema),
  });

  const handleBackClick = () => {
    navigate(ROUTES.ANNUAL_GRID_SUPPLY);
  };

  const onSubmit = async (data: SmartMeterFormValues) => {
    setAnimations((prev) => ({ ...prev, continue: !prev.continue }));

    const siteTechnicalData = getSiteTechnicalData();
    const updatedSessionData = { ...siteTechnicalData, ...data };
    setSiteTechnicalData(updatedSessionData);

    const payload: TariffSavingsPayload = {
      siteTechnicalData: {
        pvInstalled: siteTechnicalDataFromStorage.pvInstalled,
        pvPowerMaxKwp: siteTechnicalDataFromStorage.pvPowerMaxKwp,
        wallboxInstalled: siteTechnicalDataFromStorage.wallboxInstalled,

        batteryInstalled: siteTechnicalDataFromStorage.batteryInstalled,
        batterySizeKwh: siteTechnicalDataFromStorage.batterySizeKwh,

        smartMeterInstalled: JSON.parse(data.smartMeterInstalled),
      },
      userProfileData: {
        yearlyGridSupplyKwh: siteTechnicalDataFromStorage.yearlyGridSupplyKwh,
        zipcode: siteTechnicalDataFromStorage.pvZipCode,
      },
    };

    setIsLoading(true);

    try {
      const response = await tariffSavings(payload);

      if (
        response.externalPriceFetchStatus === MSG_IDS.no_prices_available ||
        response.tariffs.length === 0
      ) {
        setIsLoading(false);
        navigate(`${ROUTES.ERROR}?msgId=${response.externalPriceFetchStatus}`);
        return;
      }

      if (response.externalPriceFetchStatus === MSG_IDS.temporary_problem) {
        setIsLoading(false);
        navigate(`${ROUTES.ERROR}?msgId=${MSG_IDS.temporary_problem}`);
        return;
      }

      if (response.tariffs && !response.tariffs[0].savings) {
        setIsLoading(false);
        navigate(`${ROUTES.ERROR}?msgId=${MSG_IDS.no_prices_available}`);
        return;
      }

      const responseMapped = tariffSavingsMapped(response);
      setTariffs(responseMapped);
      if (response.bonus && response.bonus.isActive) {
        setBonus(response.bonus);
      } else {
        setBonus(null);
      }

      setTimeout(() => {
        setIsLoading(false);
        if (transferId && salesPartnerId) {
          navigate(
            ROUTES.INTRODUCTION +
              `?tid=${transferId}&spid=${salesPartnerId}&theme=${themeName}&openSummary=true`,
          );
          return;
        }
        navigate(ROUTES.INTRODUCTION + `?theme=${themeName}&openSummary=true`);
      }, 1000);
    } catch (e) {
      setIsLoading(false);
      navigate(ROUTES.ERROR);
    }
  };

  return (
    <FormContainer
      title={t("page.smartMeter.formTitle")}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Box sx={{ marginTop: 6, marginBottom: 3 }}>
        <SmartMeterForm form={form} />
      </Box>
      <FormSubmitContainer onClick={handleBackClick} isLoading={isLoading} />
    </FormContainer>
  );
};

export default SmartMeter;
